import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  FileText, Loader2, Upload, Pencil, CheckCircle2, AlertTriangle, ArrowDownToLine, ExternalLink, Sparkles, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => (n === null || n === undefined || n === "" ? null : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
export function LeaseScan({ propertyId, leaseSummary, depositAmount, onSummary, onPrefillDeposit }) {
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [failed, setFailed] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const summary = leaseSummary || null;

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setFailed(false);
    setScanning(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/properties/${propertyId}/lease-scan`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSummary?.(data);
      toast.success("Lease scanned");
    } catch (err) {
      console.error("lease scan failed", err);
      setFailed(true);
      toast.error(err?.response?.data?.detail || "Couldn't read that lease.");
    } finally { setScanning(false); }
  };

  // ---------- No summary yet ----------
  if (!summary) {
    return (
      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="lease-scan-card">
        <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={onFile} className="hidden" data-testid="lease-file-input" />
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/15 text-brand shrink-0"><FileText size={20} /></span>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-semibold">Scan my lease</h3>
            <p className="text-sm text-white/60">Upload your lease PDF and we'll pull out the deposit-relevant details — amount, return deadline, cleaning and documentation requirements.</p>
            {!scanning && !failed && (
              <Button data-testid="lease-scan-btn" onClick={pick} className="mt-3 rounded-xl bg-brand hover:bg-brand-hover text-white">
                <Upload size={16} className="mr-2" /> Upload lease PDF
              </Button>
            )}
          </div>
        </div>

        {scanning && (
          <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid="lease-scanning">
            <Loader2 size={18} className="animate-spin text-brand" />
            <p className="text-sm text-white/80">Reading your lease…</p>
          </div>
        )}

        {failed && !scanning && (
          <LeaseManualFallback propertyId={propertyId} onSummary={onSummary} onRetry={pick} />
        )}

        <p className="mt-3 text-xs text-white/40" data-testid="lease-disclaimer">This summarizes your lease's wording. It is not legal advice.</p>
      </div>
    );
  }

  // ---------- Summary ----------
  const hasList = (a) => Array.isArray(a) && a.length > 0;
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="lease-summary-card">
      <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={onFile} className="hidden" data-testid="lease-file-input" />
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-400/15 text-emerald-300 shrink-0"><FileText size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-300" /> Lease summary</h3>
            <p className="text-sm text-white/60">Deposit-relevant details from your lease. Correct anything that looks off.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" data-testid="lease-edit-btn" onClick={() => setEditOpen(true)}
          className="rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/15 shrink-0">
          <Pencil size={14} className="mr-1.5" /> Edit
        </Button>
      </div>

      <div className="mt-4 space-y-3">
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid="lease-field-deposit">
          <p className="text-xs uppercase tracking-wide text-white/40">Deposit amount</p>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <p className="text-lg font-semibold">{money(summary.deposit_amount) || <span className="text-white/40 text-base font-normal">Not stated in lease</span>}</p>
            {summary.deposit_amount != null && (depositAmount === null || depositAmount === undefined) && (
              <Button size="sm" data-testid="lease-prefill-deposit-btn" onClick={() => onPrefillDeposit?.(summary.deposit_amount)}
                className="rounded-lg h-8 bg-amber hover:bg-amber-hover text-navy font-semibold">
                <ArrowDownToLine size={14} className="mr-1" /> Use for this property
              </Button>
            )}
          </div>
        </div>

        <LeaseField label="Deposit return deadline" value={summary.return_deadline_text} testId="lease-field-deadline" />

        <LeaseListField label="Cleaning clauses" items={summary.cleaning_clauses} testId="lease-field-cleaning" />
        <LeaseListField label="Documentation requirements" items={summary.documentation_requirements} testId="lease-field-docs" />

        <LeaseField label="Move-out notice" value={summary.notice_requirements} testId="lease-field-notice" />
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid="lease-field-forwarding">
          <p className="text-xs uppercase tracking-wide text-white/40">Forwarding address required</p>
          <p className="mt-0.5 text-sm">
            {summary.forwarding_address_required === true ? "Yes" : summary.forwarding_address_required === false ? "No" : <span className="text-white/40">Not stated in lease</span>}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {summary.source_file_id && (
          <a href={`${API}/properties/${propertyId}/lease-file`} target="_blank" rel="noopener noreferrer" data-testid="lease-view-source">
            <Button variant="outline" size="sm" className="rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/15"><ExternalLink size={14} className="mr-1.5" /> View lease PDF</Button>
          </a>
        )}
        <Button variant="ghost" size="sm" data-testid="lease-rescan-btn" onClick={pick} disabled={scanning} className="rounded-xl text-white/70 hover:bg-white/10">
          {scanning ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Reading…</> : <><Sparkles size={14} className="mr-1.5" /> Re-scan a different lease</>}
        </Button>
      </div>

      <p className="mt-3 text-xs text-white/40" data-testid="lease-disclaimer">This summarizes your lease's wording. It is not legal advice.</p>

      <LeaseEditDialog open={editOpen} onOpenChange={setEditOpen} propertyId={propertyId} summary={summary} onSummary={onSummary} />
    </div>
  );
}

function LeaseField({ label, value, testId, children }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-white/40">{label}</p>
      <p className="mt-0.5 text-sm">{value || <span className="text-white/40">Not stated in lease</span>}</p>
      {children}
    </div>
  );
}

function LeaseListField({ label, items, testId }) {
  const has = Array.isArray(items) && items.length > 0;
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3" data-testid={testId}>
      <p className="text-xs uppercase tracking-wide text-white/40">{label}</p>
      {has ? (
        <ul className="mt-1 space-y-1.5">
          {items.map((it, i) => (
            <li key={`${i}-${it.slice(0, 12)}`} className="text-sm text-white/85 flex gap-2"><span className="text-brand mt-0.5">•</span><span>{it}</span></li>
          ))}
        </ul>
      ) : (
        <p className="mt-0.5 text-sm text-white/40">None found in lease</p>
      )}
    </div>
  );
}

function LeaseManualFallback({ propertyId, onSummary, onRetry }) {
  const [deposit, setDeposit] = useState("");
  const [deadline, setDeadline] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      const d = deposit.replace(/[^0-9.]/g, "");
      if (d) body.deposit_amount = Number(d);
      if (deadline.trim()) body.return_deadline_text = deadline.trim();
      const { data } = await api.patch(`/properties/${propertyId}/lease-summary`, body);
      onSummary?.(data);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-amber-400/40 bg-amber-400/10 p-4" data-testid="lease-manual-fallback">
      <p className="text-sm font-semibold text-amber-100 flex items-center gap-2"><AlertTriangle size={16} /> We couldn't read that lease</p>
      <p className="text-xs text-amber-100/80 mt-1">It may be a scanned image or a protected PDF. You can enter the key details by hand, or try another file.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-white/70">Deposit amount</Label>
          <Input data-testid="lease-manual-deposit" inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.target.value)} placeholder="e.g. 1500" className="mt-1 h-11 rounded-xl" />
        </div>
        <div>
          <Label className="text-xs text-white/70">Deposit return deadline</Label>
          <Input data-testid="lease-manual-deadline" value={deadline} onChange={(e) => setDeadline(e.target.value)} placeholder="e.g. within 21 days of move-out" className="mt-1 h-11 rounded-xl" />
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button data-testid="lease-manual-save" onClick={save} disabled={saving} className="rounded-xl bg-brand hover:bg-brand-hover text-white">
          {saving ? <Loader2 size={16} className="animate-spin" /> : "Save details"}
        </Button>
        <Button variant="outline" data-testid="lease-manual-retry" onClick={onRetry} className="rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/15">Try another PDF</Button>
      </div>
    </div>
  );
}

function LeaseEditDialog({ open, onOpenChange, propertyId, summary, onSummary }) {
  const [deposit, setDeposit] = useState(summary.deposit_amount ?? "");
  const [deadline, setDeadline] = useState(summary.return_deadline_text ?? "");
  const [cleaning, setCleaning] = useState((summary.cleaning_clauses || []).join("\n"));
  const [docs, setDocs] = useState((summary.documentation_requirements || []).join("\n"));
  const [notice, setNotice] = useState(summary.notice_requirements ?? "");
  const [forwarding, setForwarding] = useState(
    summary.forwarding_address_required === true ? "yes" : summary.forwarding_address_required === false ? "no" : "unknown");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const d = String(deposit).replace(/[^0-9.]/g, "");
      const body = {
        deposit_amount: d ? Number(d) : null,
        return_deadline_text: deadline.trim() || null,
        cleaning_clauses: cleaning.split("\n").map((s) => s.trim()).filter(Boolean),
        documentation_requirements: docs.split("\n").map((s) => s.trim()).filter(Boolean),
        notice_requirements: notice.trim() || null,
        forwarding_address_required: forwarding === "yes" ? true : forwarding === "no" ? false : null,
      };
      const { data } = await api.patch(`/properties/${propertyId}/lease-summary`, body);
      onSummary?.(data);
      onOpenChange(false);
      toast.success("Lease details updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lease-edit-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit lease details</DialogTitle>
          <DialogDescription>Correct any field. One item per line for the lists.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Deposit amount</Label>
              <Input data-testid="lease-edit-deposit" inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.target.value)} className="mt-1 h-10 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs">Forwarding address required</Label>
              <select data-testid="lease-edit-forwarding" value={forwarding} onChange={(e) => setForwarding(e.target.value)}
                className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
                <option value="unknown">Not stated</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Deposit return deadline</Label>
            <Input data-testid="lease-edit-deadline" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs">Cleaning clauses (one per line)</Label>
            <Textarea data-testid="lease-edit-cleaning" value={cleaning} onChange={(e) => setCleaning(e.target.value)} rows={3} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs">Documentation requirements (one per line)</Label>
            <Textarea data-testid="lease-edit-docs" value={docs} onChange={(e) => setDocs(e.target.value)} rows={3} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs">Move-out notice</Label>
            <Textarea data-testid="lease-edit-notice" value={notice} onChange={(e) => setNotice(e.target.value)} rows={2} className="mt-1 rounded-xl" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="lease-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="lease-edit-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
